import * as React from 'react';
import Avatar from 'antd/lib/avatar';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { useDispatch, useSelector } from 'react-redux';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    EditOutlined,
    LoadingOutlined,
    ToolOutlined,
} from '@ant-design/icons';
import {
    FixRequest,
    FixRequestFormFieldValue,
    FixRequestStatusType,
    IssueEntity,
    IssueFixRequest,
    IssueState,
    ReleaseStatus,
} from '@/types';
import { RootState } from '@/reducers/reducers';
import {
    antGrey,
    drawerSectionSeparator,
    getAvatarColorForName,
    runwayColors,
    rwColors,
} from '@/components/styles/globalStyles';
import {
    selectAppTimeFormatSetting,
    selectEffectiveTimezone,
    selectRelease,
    selectReleasesForAppId,
    selectRequiredFixRequestsApprovals,
    selectUserById,
    selectUserCanPerformAction,
} from '@/selectors/selectors';
import { GetFixRequestCurrentApprovals, getFixRequestFormFieldId, runeCount } from '@/helpers';
import MarkdownComponent from '@/components/HOCs/Markdown';
import MarkdownEditor from '@/components/HOCs/MarkdownEditor';
import { displayUserNameLong } from '@/helpers/displayHelpers';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import {
    TRY_TRANSITION_ISSUE,
    TRY_UPDATE_ISSUE_FIX_REQUEST,
    TriageViewType,
    tryTransitionIssue,
    tryUpdateIssueFixRequest,
} from '@/actions/triage';
import { ReleaseStepUpdatable } from '@/actions/updatable';
import { tryUpdateReleaseStep } from '@/actions/releaseStep';
import { useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import PermissionedActionButton, { DISALLOWED_ACTION_ERROR_MESSAGE } from '@/components/HOCs/PermissionedActionButton';
import TimeWithConditionalTz from '@/components/common/TimeWithConditionalTz';
import { formatDateWithTimezone } from '@/timeHelpers';
import { dayjs } from '@/extendedDayjs';
import { FIX_VERSION_CODE_LOCK_TOOLTIP, fixRequestStatusMeta, LABEL_STYLE } from './FixRequestModule';

export interface FeatureReadinessFixContext {
    appId: string;
    releaseId: string;
    stepRunId: string;
    fixRequest: FixRequest;
    featureItemId: string;
    isAutoIgnoreItem: boolean;
    requiresFixRequestsApprovals: boolean;
}

interface FixRequestSectionProps {
    orgId: string;
    issue?: IssueEntity;
    viewType?: TriageViewType;
    featureReadiness?: FeatureReadinessFixContext;
    disabled?: boolean;
    onVersionMoveStarted?: (targetVersion: string) => void;
}

export const FixRequestSection: React.FC<FixRequestSectionProps> = ({
    orgId,
    issue,
    viewType,
    featureReadiness,
    disabled,
    onVersionMoveStarted,
}) => {
    const dispatch = useDispatch();
    const fixRequest: IssueFixRequest | undefined = issue ? issue.fixRequest : featureReadiness?.fixRequest;
    const appId = issue?.appId ?? featureReadiness?.appId ?? '';
    // Without an issue, map the fix's own status onto the equivalent IssueState so the status meta
    // and approve/reject visibility logic applies unchanged.
    const displayState: IssueState =
        issue ? issue.state
        : fixRequest?.status?.status === FixRequestStatusType.FixRequestApproved ? IssueState.FixApproved
        : fixRequest?.status?.status === FixRequestStatusType.FixRequestRejected ? IssueState.FixRejected
        : IssueState.FixRequested;
    const requester = useSelector((state: RootState) =>
        fixRequest?.createdBy ? selectUserById(state, orgId, appId, fixRequest.createdBy) : undefined,
    );
    const requesterName = requester ? displayUserNameLong(requester) : (fixRequest?.createdBy ?? '');
    const requiredApprovals = useSelector(selectRequiredFixRequestsApprovals(appId)) || 1;
    const currentApprovals = fixRequest?.status ? GetFixRequestCurrentApprovals(fixRequest.status) : 0;
    const showApprovalCount = requiredApprovals > 1;
    const showApprovalControls = !featureReadiness || featureReadiness.requiresFixRequestsApprovals;
    const canApproveReject = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAUpdateFixRequestStatus),
    );
    const canManage = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAManageTriageIssues),
    );
    // Feature readiness mode: version edits are gated by the release-scoped permission (like the
    // other fix edits there), not the triage-manage permission.
    const canEditReleaseFix = useSelector((state: RootState) =>
        featureReadiness ? selectUserCanPerformAction(state, undefined, featureReadiness.releaseId) : false,
    );
    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, appId));
    const releaseVersion = useSelector((state: RootState) =>
        featureReadiness ? selectRelease(state, featureReadiness.releaseId)?.version : undefined,
    );
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));

    // Loading/success keys differ per mode; the hooks keyed with the other mode's (undefined) id
    // never match and stay false.
    const isTransitioningIssue = useIsLoading(TRY_TRANSITION_ISSUE, issue?.id);
    const approveIsLoading = useIsLoading(ReleaseStepUpdatable.ApproveFixRequest, featureReadiness?.fixRequest.id);
    const rejectIsLoading = useIsLoading(ReleaseStepUpdatable.RejectFixRequest, featureReadiness?.fixRequest.id);
    const rescindIsLoading = useIsLoading(
        ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
        featureReadiness?.fixRequest.id,
    );
    const isTransitioning = issue ? isTransitioningIssue : approveIsLoading || rejectIsLoading || rescindIsLoading;
    const isUpdatingIssueFixRequest = useIsLoading(TRY_UPDATE_ISSUE_FIX_REQUEST, issue?.id);
    // Field edits and version moves both go through UpdateFixRequest in feature readiness mode.
    const updateFieldsIsLoading = useIsLoading(ReleaseStepUpdatable.UpdateFixRequest, featureReadiness?.fixRequest.id);
    const isUpdatingFixRequest = isUpdatingIssueFixRequest || updateFieldsIsLoading;
    const issueFixRequestUpdated = useUpdated(TRY_UPDATE_ISSUE_FIX_REQUEST, issue?.id);
    const releaseFixRequestUpdated = useUpdated(ReleaseStepUpdatable.UpdateFixRequest, featureReadiness?.fixRequest.id);
    const fixRequestUpdated = issue ? issueFixRequestUpdated : releaseFixRequestUpdated;

    const fixVersionOptions = releases
        .filter(
            (release) =>
                ![ReleaseStatus.Completed, ReleaseStatus.Skipped, ReleaseStatus.Deleted].includes(release.status),
        )
        .map((release) => ({ value: release.version, label: release.displayableVersion || release.version }));
    const codeChangesLockVersion = !!fixRequest?.hasCodeChangesPendingOrMerged;
    const canEditVersionPermission = issue ? canManage : canEditReleaseFix;
    const canEditFixVersion =
        canEditVersionPermission && !disabled && displayState !== IssueState.FixMerged && !codeChangesLockVersion;
    const canAct = canApproveReject && !disabled;
    const canRescind = canApproveReject;
    const fixVersionTooltip =
        codeChangesLockVersion ? FIX_VERSION_CODE_LOCK_TOOLTIP
        : !canEditVersionPermission ? DISALLOWED_ACTION_ERROR_MESSAGE
        : undefined;
    const currentVersion = issue ? fixRequest?.fixVersion : releaseVersion;

    const editPermissionProps =
        issue ? { actionType: RunwayUserAction.UAManageTriageIssues } : { releaseId: featureReadiness?.releaseId };
    const statusUpdatedAt =
        showApprovalControls && displayState !== IssueState.FixRequested ? fixRequest?.status?.updatedAt : undefined;

    // The fix snapshots its form field settings at creation; edits validate against that snapshot.
    const fixRequestFormFields = fixRequest?.formFieldsSettings ?? [];

    // Per-field inline editing: only one field is editable at a time.
    const [editingFieldId, setEditingFieldId] = React.useState<string | null>(null);
    const [editingFieldValue, setEditingFieldValue] = React.useState<string>('');
    const [editError, setEditError] = React.useState<string | null>(null);
    const [currentFormFieldValues, setCurrentFormFieldValues] = React.useState<FixRequestFormFieldValue[]>(
        fixRequest?.formFieldsValues ?? [],
    );
    const pendingFormFieldValuesRef = React.useRef<FixRequestFormFieldValue[] | null>(null);
    // Tracks whether the caret has been placed at the end of the text for the field just opened, so
    // autofocus lands at the end of any existing value rather than position 0 (and only on first focus).
    const caretPlacedRef = React.useRef(false);

    // The state only captures values at mount — resync on outside changes, but never mid-edit.
    const externalFormFieldValues = fixRequest?.formFieldsValues;
    React.useEffect(() => {
        if (!editingFieldId) {
            setCurrentFormFieldValues(externalFormFieldValues ?? []);
        }
    }, [externalFormFieldValues, editingFieldId]);

    // Live validation — runs whenever the editing field or its value changes.
    React.useEffect(() => {
        if (!editingFieldId) return;
        const field = fixRequestFormFields.find((f) => getFixRequestFormFieldId(f) === editingFieldId);
        if (!field) return;
        const trimmed = editingFieldValue.trim();
        const trimmedRuneCount = runeCount(trimmed);
        if (field.required && trimmed.length === 0) {
            setEditError(`${field.name} is required`);
        } else if (
            field.minCharacters &&
            field.minCharacters > 0 &&
            trimmedRuneCount > 0 &&
            trimmedRuneCount < field.minCharacters
        ) {
            setEditError(`${field.name} is too short (${trimmedRuneCount}/${field.minCharacters})`);
        } else {
            setEditError(null);
        }
    }, [editingFieldId, editingFieldValue]);

    const startEditingField = (fieldId: string, currentValue: string) => {
        setEditingFieldValue(currentValue);
        setEditError(null);
        caretPlacedRef.current = false;
        setEditingFieldId(fieldId);
    };

    const saveFieldEdit = () => {
        const fieldDef = fixRequestFormFields.find((f) => getFixRequestFormFieldId(f) === editingFieldId);
        if (fieldDef?.required && !editingFieldValue.trim()) {
            setEditError(`${fieldDef.name} is required`);
            return;
        }
        const trimmedRuneCount = runeCount(editingFieldValue.trim());
        if (fieldDef?.minCharacters && fieldDef.minCharacters > 0 && trimmedRuneCount < fieldDef.minCharacters) {
            setEditError(`${fieldDef.name} is too short (${trimmedRuneCount}/${fieldDef.minCharacters})`);
            return;
        }
        setEditError(null);
        const formFieldsValues: FixRequestFormFieldValue[] = fixRequestFormFields.map((field) => {
            const fieldId = getFixRequestFormFieldId(field);
            if (fieldId === editingFieldId) {
                return { fieldId, value: editingFieldValue };
            }
            const existing = currentFormFieldValues.find((v) => v.fieldId === fieldId);
            return { fieldId, value: existing?.value ?? '' };
        });
        pendingFormFieldValuesRef.current = formFieldsValues;
        if (issue) {
            dispatch(tryUpdateIssueFixRequest({ orgId, appId, issueId: issue.id, formFieldsValues }));
        } else if (featureReadiness) {
            dispatch(
                tryUpdateReleaseStep(
                    ReleaseStepUpdatable.UpdateFixRequest,
                    {
                        appId: featureReadiness.appId,
                        orgId,
                        releaseId: featureReadiness.releaseId,
                        stepRunId: featureReadiness.stepRunId,
                        frItemId: featureReadiness.featureItemId,
                    },
                    { formFieldsValues, fixId: featureReadiness.fixRequest.id },
                    featureReadiness.fixRequest.id,
                ),
            );
        }
        // Field stays open — closed by the success effect below.
    };

    // On a successful update, commit the optimistic values and close the active field editor.
    React.useEffect(() => {
        if (fixRequestUpdated && editingFieldId) {
            if (pendingFormFieldValuesRef.current !== null) {
                setCurrentFormFieldValues(pendingFormFieldValuesRef.current);
                pendingFormFieldValuesRef.current = null;
            }
            setEditingFieldId(null);
        }
    }, [fixRequestUpdated]);

    const transition = (targetState: IssueState) => {
        if (issue) {
            dispatch(tryTransitionIssue({ orgId, appId, issueId: issue.id, targetState, viewType }));
            return;
        }
        if (!featureReadiness) {
            return;
        }
        const resourceIds = {
            appId: featureReadiness.appId,
            orgId,
            releaseId: featureReadiness.releaseId,
            stepRunId: featureReadiness.stepRunId,
            frItemId: featureReadiness.featureItemId,
        };
        const request = { fixId: featureReadiness.fixRequest.id, isAutoIgnoreItem: featureReadiness.isAutoIgnoreItem };
        if (targetState === IssueState.FixApproved) {
            dispatch(
                tryUpdateReleaseStep(
                    ReleaseStepUpdatable.ApproveFixRequest,
                    resourceIds,
                    request,
                    featureReadiness.fixRequest.id,
                ),
            );
        } else if (targetState === IssueState.FixRejected) {
            dispatch(
                tryUpdateReleaseStep(
                    ReleaseStepUpdatable.RejectFixRequest,
                    resourceIds,
                    request,
                    featureReadiness.fixRequest.id,
                ),
            );
        } else {
            dispatch(
                tryUpdateReleaseStep(
                    ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
                    resourceIds,
                    request,
                    featureReadiness.fixRequest.id,
                ),
            );
        }
    };

    if (!fixRequest) {
        return null;
    }

    return (
        <>
            <Collapse
                className="triage-collapse"
                ghost
                bordered={false}
                defaultActiveKey={['section']}
                items={[
                    {
                        key: 'section',
                        showArrow: false,
                        style: { borderBottom: drawerSectionSeparator },
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <ToolOutlined style={{ color: antGrey.grey7 }} />
                                <Text style={{ color: antGrey.grey8, fontSize: 14 }}>
                                    {showApprovalControls ? 'Fix request' : 'Fix'}
                                </Text>
                            </div>
                        ),
                        extra:
                            showApprovalControls ?
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {fixRequestStatusMeta(displayState).icon}
                                    <Text style={LABEL_STYLE}>
                                        {fixRequestStatusMeta(displayState).label}
                                        {(
                                            showApprovalCount &&
                                            (displayState === IssueState.FixRequested ||
                                                displayState === IssueState.FixApproved)
                                        ) ?
                                            ` (${currentApprovals}/${requiredApprovals})`
                                        :   ''}
                                    </Text>
                                </div>
                            :   undefined,
                        children: (
                            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {fixRequestFormFields.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        {fixRequestFormFields.map((field) => {
                                            const fieldId = getFixRequestFormFieldId(field);
                                            const displayVal = currentFormFieldValues.find(
                                                (v) => v.fieldId === fieldId,
                                            )?.value;
                                            const isEditing = editingFieldId === fieldId;

                                            return (
                                                <div key={fieldId} style={{ width: '100%' }}>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            marginBottom: 6,
                                                        }}
                                                    >
                                                        <Text
                                                            style={{
                                                                textTransform: 'uppercase',
                                                                fontSize: 11,
                                                                fontWeight: 600,
                                                                letterSpacing: '0.1em',
                                                                color: antGrey.grey7,
                                                            }}
                                                        >
                                                            {field.name}
                                                            {isEditing && field.required && (
                                                                <Text
                                                                    type="danger"
                                                                    style={{ marginLeft: 3, fontSize: 11 }}
                                                                >
                                                                    *
                                                                </Text>
                                                            )}
                                                        </Text>
                                                        {!isEditing && (
                                                            <PermissionedActionButton
                                                                type="link"
                                                                size="small"
                                                                {...editPermissionProps}
                                                                style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                                                                icon={<EditOutlined />}
                                                                onClick={(e) => {
                                                                    startEditingField(fieldId, displayVal ?? '');
                                                                    e.stopPropagation();
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    {isEditing ?
                                                        <>
                                                            <div style={{ marginBottom: 4 }}>
                                                                <MarkdownEditor
                                                                    autoFocus
                                                                    preview="edit"
                                                                    value={editingFieldValue}
                                                                    onChange={(val) => setEditingFieldValue(val ?? '')}
                                                                    textareaProps={{
                                                                        disabled: isUpdatingFixRequest,
                                                                        placeholder: field.placeholder || undefined,
                                                                        // On the initial autofocus, move the caret to
                                                                        // the end of any existing text instead of 0.
                                                                        onFocus: (e) => {
                                                                            if (caretPlacedRef.current) return;
                                                                            caretPlacedRef.current = true;
                                                                            const len = e.target.value.length;
                                                                            e.target.setSelectionRange(len, len);
                                                                        },
                                                                    }}
                                                                />
                                                            </div>
                                                            {editError && (
                                                                <Text
                                                                    type="danger"
                                                                    style={{ display: 'block', marginBottom: 12 }}
                                                                >
                                                                    {editError}
                                                                </Text>
                                                            )}
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'flex-end',
                                                                    gap: 4,
                                                                    marginTop: 8,
                                                                }}
                                                            >
                                                                <Button
                                                                    type="link"
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        setEditingFieldId(null);
                                                                        setEditError(null);
                                                                        e.stopPropagation();
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                                <PermissionedActionButton
                                                                    type="primary"
                                                                    size="small"
                                                                    {...editPermissionProps}
                                                                    loading={isUpdatingFixRequest}
                                                                    onClick={(e) => {
                                                                        saveFieldEdit();
                                                                        e.stopPropagation();
                                                                    }}
                                                                >
                                                                    Save
                                                                </PermissionedActionButton>
                                                            </div>
                                                        </>
                                                    : displayVal ?
                                                        <MarkdownComponent
                                                            source={displayVal}
                                                            style={{
                                                                fontSize: 14,
                                                                color: antGrey.grey8,
                                                                paddingTop: 4,
                                                                // Keeps the first line clear of the edit button.
                                                                paddingRight: 28,
                                                            }}
                                                        />
                                                    :   <Text type="secondary" style={{ display: 'block' }}>
                                                            -
                                                        </Text>
                                                    }
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 6,
                                            flex: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Target version</Text>
                                        <Tooltip title={fixVersionTooltip}>
                                            <Select
                                                value={currentVersion}
                                                options={fixVersionOptions}
                                                showSearch
                                                optionFilterProp="label"
                                                disabled={isUpdatingFixRequest || !canEditFixVersion}
                                                suffixIcon={
                                                    isUpdatingFixRequest ?
                                                        <LoadingOutlined spin style={{ color: antGrey.grey6 }} />
                                                    :   undefined
                                                }
                                                placeholder="--"
                                                style={{ width: '100%' }}
                                                onChange={(value) => {
                                                    if (issue) {
                                                        dispatch(
                                                            tryUpdateIssueFixRequest({
                                                                orgId,
                                                                appId,
                                                                issueId: issue.id,
                                                                version: value,
                                                            }),
                                                        );
                                                        return;
                                                    }
                                                    if (!featureReadiness) {
                                                        return;
                                                    }
                                                    onVersionMoveStarted?.(value);
                                                    dispatch(
                                                        tryUpdateReleaseStep(
                                                            ReleaseStepUpdatable.UpdateFixRequest,
                                                            {
                                                                appId: featureReadiness.appId,
                                                                orgId,
                                                                releaseId: featureReadiness.releaseId,
                                                                stepRunId: featureReadiness.stepRunId,
                                                                frItemId: featureReadiness.featureItemId,
                                                            },
                                                            {
                                                                fixId: featureReadiness.fixRequest.id,
                                                                version: value,
                                                            },
                                                            featureReadiness.fixRequest.id,
                                                        ),
                                                    );
                                                }}
                                            />
                                        </Tooltip>
                                    </div>
                                    {requesterName && (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 6,
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Requested by</Text>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minHeight: 32,
                                                    padding: '3px 4px',
                                                    width: '100%',
                                                    boxSizing: 'border-box',
                                                    border: `1px solid ${antGrey.grey5}`,
                                                    borderRadius: 6,
                                                    background: antGrey.grey2,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        padding: '1px 8px',
                                                        borderRadius: 4,
                                                        background: antGrey.grey3,
                                                    }}
                                                >
                                                    <Avatar
                                                        size={20}
                                                        src={requester?.commsAvatars?.image32}
                                                        style={{
                                                            backgroundColor: getAvatarColorForName(requesterName),
                                                            fontSize: 11,
                                                            border: 'none',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {requesterName.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Text style={{ color: antGrey.grey8, fontSize: 14 }}>
                                                        {requesterName}
                                                    </Text>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {showApprovalControls && displayState === IssueState.FixRequested && (
                                    <div style={{ display: 'flex', gap: 12 }}>
                                        <Tooltip title={canApproveReject ? undefined : DISALLOWED_ACTION_ERROR_MESSAGE}>
                                            <Button
                                                type="primary"
                                                icon={<CheckCircleOutlined />}
                                                loading={isTransitioning}
                                                disabled={!canAct}
                                                onClick={() => transition(IssueState.FixApproved)}
                                                style={{
                                                    flex: 1,
                                                    height: 40,
                                                    backgroundColor: canAct ? rwColors.statusGreen : undefined,
                                                    borderColor: canAct ? rwColors.statusGreen : undefined,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Approve fix request
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title={canApproveReject ? undefined : DISALLOWED_ACTION_ERROR_MESSAGE}>
                                            <Button
                                                icon={<CloseCircleOutlined />}
                                                loading={isTransitioning}
                                                disabled={!canAct}
                                                onClick={() => transition(IssueState.FixRejected)}
                                                style={{
                                                    flex: 1,
                                                    height: 40,
                                                    backgroundColor: canAct ? rwColors.statusRed : undefined,
                                                    borderColor: canAct ? rwColors.statusRed : undefined,
                                                    color: canAct ? runwayColors.white : undefined,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Reject fix request
                                            </Button>
                                        </Tooltip>
                                        {showApprovalCount && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    color: rwColors.statusYellow,
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <ClockCircleOutlined />
                                                <Text style={{ color: antGrey.grey8, fontWeight: 600, fontSize: 20 }}>
                                                    {currentApprovals} of {requiredApprovals}
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {showApprovalControls &&
                                    (displayState === IssueState.FixApproved ||
                                        displayState === IssueState.FixRejected) && (
                                        <div style={{ display: 'flex', gap: 12 }}>
                                            {displayState === IssueState.FixApproved && (
                                                <Button
                                                    icon={<CheckCircleOutlined />}
                                                    style={{
                                                        flex: 1,
                                                        height: 40,
                                                        backgroundColor: rwColors.statusGreen,
                                                        borderColor: rwColors.statusGreen,
                                                        color: runwayColors.white,
                                                        fontWeight: 600,
                                                        opacity: 0.6,
                                                        pointerEvents: 'none',
                                                    }}
                                                >
                                                    Approved
                                                </Button>
                                            )}
                                            <Tooltip
                                                title={canApproveReject ? undefined : DISALLOWED_ACTION_ERROR_MESSAGE}
                                            >
                                                <Button
                                                    icon={<ClockCircleOutlined />}
                                                    loading={isTransitioning}
                                                    disabled={!canRescind}
                                                    onClick={() => transition(IssueState.FixRequested)}
                                                    style={{
                                                        flex: 1,
                                                        height: 40,
                                                        color: canRescind ? rwColors.statusYellow : undefined,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {displayState === IssueState.FixApproved ?
                                                        'Rescind approval'
                                                    :   'Rescind rejection'}
                                                </Button>
                                            </Tooltip>
                                            {displayState === IssueState.FixRejected && (
                                                <Button
                                                    icon={<CloseCircleOutlined />}
                                                    style={{
                                                        flex: 1,
                                                        height: 40,
                                                        backgroundColor: rwColors.statusRed,
                                                        borderColor: rwColors.statusRed,
                                                        color: runwayColors.white,
                                                        fontWeight: 600,
                                                        opacity: 0.6,
                                                        pointerEvents: 'none',
                                                    }}
                                                >
                                                    Rejected
                                                </Button>
                                            )}
                                            {displayState === IssueState.FixApproved && showApprovalCount && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <CheckCircleOutlined style={{ color: rwColors.statusGreen }} />
                                                    <Text
                                                        style={{
                                                            color: antGrey.grey8,
                                                            fontWeight: 600,
                                                            fontSize: 20,
                                                        }}
                                                    >
                                                        {currentApprovals} of {requiredApprovals}
                                                    </Text>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                {(fixRequest.createdAt || statusUpdatedAt) && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {fixRequest.createdAt && (
                                            <Text style={{ color: antGrey.grey7, fontSize: 12 }}>
                                                Created{' '}
                                                <TimeWithConditionalTz
                                                    triggeredAtLabel={formatDateWithTimezone(
                                                        fixRequest.createdAt,
                                                        'longMonthDateYearTime',
                                                        effectiveTimezone,
                                                        use24HourTimeFormat,
                                                    )}
                                                    triggeredByMoment={dayjs(fixRequest.createdAt)}
                                                    use24HourTimeFormat={use24HourTimeFormat}
                                                    effectiveTimezone={effectiveTimezone}
                                                    style={{ color: 'inherit', fontSize: 'inherit' }}
                                                />
                                            </Text>
                                        )}
                                        {statusUpdatedAt && (
                                            <Text style={{ color: antGrey.grey7, fontSize: 12 }}>
                                                Updated{' '}
                                                <TimeWithConditionalTz
                                                    triggeredAtLabel={formatDateWithTimezone(
                                                        statusUpdatedAt,
                                                        'longMonthDateYearTime',
                                                        effectiveTimezone,
                                                        use24HourTimeFormat,
                                                    )}
                                                    triggeredByMoment={dayjs(statusUpdatedAt)}
                                                    use24HourTimeFormat={use24HourTimeFormat}
                                                    effectiveTimezone={effectiveTimezone}
                                                    style={{ color: 'inherit', fontSize: 'inherit' }}
                                                />
                                            </Text>
                                        )}
                                    </div>
                                )}
                            </div>
                        ),
                    },
                ]}
            />
        </>
    );
};
